//#region IMPORTS
import {
  InstoreTemplate,
  InstoreTemplateRow,
  InstoreTemplateRequest,
  InstoreTemplateTableRow,
} from "../../models/instoreTemplate";
import {
  updateInstoreTemplateAsync,
  instoreTemplateRowSelectors,
  removeInstoreTemplateRowLocal,
  upsertInstoreTemplateRowLocal,
  upsertManyInstoreTemplateRowLocal,
  removeManyInstoreTemplateRowLocal,
} from "../../features/instoreTemplates/instoreTemplatesSlice";
import {
  CREATE_TEMPLATE_ROW_MODAL,
  UPDATE_TEMPLATE_SPECS_MODAL,
} from "../../models/_consts";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, useMemo } from "react";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import { toBackendDate } from "../../helpers/toBackendDate";
import { InstoreTheme } from "../../models/instoreTheme";
import { v4 as uuidv4 } from "uuid";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { toast } from "react-toastify";
import InstoreTemplateDetailTable from "./InstoreTemplateDetailTable";
import InstoreTemplateRowForm from "./InstoreTemplateRowForm";
import InstoreTemplateForm from "../instoreTemplates/InstoreTemplateForm";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../_layout/MainContainer";
import GoBackButton from "../genericButtons/goBackButton/GoBackButton";
import useHQmanager from "../../hooks/useHQmanager";
import PageHeader from "../genericPageHeader/PageHeader";
import classNames from "../../helpers/classNames";
import MainModal from "../_layout/MainModal";

let tempLocalId = -1;
const getTempId = () => tempLocalId--;
//#endregion

//#region INTERFACE
interface Props {
  instoreTemplate?: InstoreTemplate;
  create?: boolean;
  copy?: boolean;
}
//#endregion

export default function InstoreTemplateDetailContainer({
  instoreTemplate,
  create = false,
  copy = false,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { loading: loadingThemes } = useAppSelector(
    ({ instoreThemes }) => instoreThemes
  );
  const { status, loading, instoreTemplateRowsLoaded } = useAppSelector(
    ({ instoreTemplates }) => instoreTemplates
  );

  const instoreTemplateRows = useAppSelector(
    instoreTemplateRowSelectors.selectAll
  );

  const { user } = useAppSelector(({ account }) => account);

  // Roles
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);

  // Distinct store formats
  const distinctStoreFormats: { id: number; name: string }[] = useMemo(() => {
    if (!instoreTemplateRowsLoaded) return [];

    const map = new Map<string, { id: number; name: string }>();

    instoreTemplateRows.forEach((item) => {
      const id = item.store_format_id ?? 0;
      const name = item.store_format ?? "";
      const key = String(id !== 0 ? id : name);
      if (!map.has(key)) {
        map.set(key, { id, name });
      }
    });

    return Array.from(map.values());
  }, [instoreTemplateRows, instoreTemplateRowsLoaded]);
  //#endregion

  //#region SET TABLE DATA
  const tableData = useMemo(() => {
    if (!instoreTemplateRows) return [];

    const rows: InstoreTemplateTableRow[] = [];
    let parentIndex = 10000;

    // Group by theme
    const grouped = new Map<string | number, any[]>();
    for (const item of instoreTemplateRows) {
      const key = item.theme_id ?? item.theme ?? `theme_${String(item.id)}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(item);
    }

    for (const items of grouped.values()) {
      if (!items.length) continue;

      const themeInfo = items[0];

      // Group by article
      const articles = new Map<string, any[]>();
      for (const it of items) {
        const aKey =
          (it.article_name ?? "").trim() || `article_${String(it.id)}`;
        if (!articles.has(aKey)) articles.set(aKey, []);
        articles.get(aKey)!.push(it);
      }

      const children: InstoreTemplateTableRow[] = [];
      let childCounter = 0;

      for (const articleItems of articles.values()) {
        const first = articleItems[0];

        const store_formats_amounts = articleItems.map((ai) => ({
          db_id: ai.id,
          store_format: ai.store_format,
          store_format_id: ai.store_format_id,
          amount: ai.amount,
        }));

        children.push({
          id: articleItems.map((ai) => ai.id).join("_") ?? uuidv4(),
          index: parentIndex + childCounter + 1,
          theme: first.theme,
          theme_id: first.theme_id,
          article_name: first.article_name,
          parent: false,
          store_formats_amounts,
        });

        childCounter++;
      }

      rows.push({
        id: `${themeInfo.theme}_${themeInfo.theme_id}`,
        index: parentIndex,
        theme: themeInfo.theme,
        theme_id: themeInfo.theme_id,
        article_name: themeInfo.theme,
        parent: true,
        store_formats_amounts: [],
        children,
      });

      parentIndex += 10000;
    }

    return rows;
  }, [instoreTemplateRows]);
  //#endregion

  //#region ADD THEME ROW
  const addThemeRowModalClickHandler = () => {
    dispatch(openModal(CREATE_TEMPLATE_ROW_MODAL));
  };

  const addThemeRowHandler = async (theme: InstoreTheme) => {
    const newParentIndex =
      tableData.length > 0
        ? Math.max(...tableData.map((r) => r.index ?? 0)) + 10000
        : 10000;

    // Persist to Redux store only
    if (instoreTemplate && instoreTemplate.id) {
      const updated = { ...instoreTemplate } as any;
      updated.template_rows = Array.isArray(updated.template_rows)
        ? [...updated.template_rows]
        : [];

      // Create a new local backend-like row for the template_rows array
      const newLocalRow = {
        id: getTempId(),
        template_id: updated.id,
        theme: theme.name,
        theme_id: theme.id,
        store_format: distinctStoreFormats[0].name,
        store_format_id: distinctStoreFormats[0].id,
        article_name: "enter name",
        amount: 0,
        index: newParentIndex + 1,
      };

      dispatch(upsertInstoreTemplateRowLocal(newLocalRow));
    }

    // Close modal
    dispatch(openModal(false));
  };
  //#endregion

  //#region ADD ARTICLE ROW
  const addArticleRowHandler = (themeId: number) => {
    // Compute new child index using current localTableData
    const parentRow = tableData.find((r) => r.theme_id === themeId && r.parent);
    const maxChildIndex = parentRow
      ? parentRow.children && parentRow.children.length > 0
        ? Math.max(...parentRow.children.map((c) => c.index ?? 0))
        : (parentRow.index ?? 0) + 1
      : Date.now();

    // Persist the new child to the Redux instoreTemplate.template_rows (local only)
    if (instoreTemplate && instoreTemplate.id) {
      const updated = { ...instoreTemplate } as any;
      updated.template_rows = Array.isArray(updated.template_rows)
        ? [...updated.template_rows]
        : [];

      const defaultFormat =
        distinctStoreFormats && distinctStoreFormats.length > 0
          ? distinctStoreFormats[0]
          : { id: 0, name: "" };

      const newLocalRow = {
        id: getTempId(),
        template_id: updated.id,
        theme: parentRow?.theme ?? "",
        theme_id: themeId,
        store_format: defaultFormat.name,
        store_format_id: defaultFormat.id,
        article_name: "enter a name",
        amount: 0,
        index: maxChildIndex + 1,
      };

      dispatch(upsertInstoreTemplateRowLocal(newLocalRow));
    }
  };
  //#endregion

  //#region UPDATE CHANGES TO TEMPLATE ROWS
  const saveLocalStateChangesHandler = (
    columnId: string,
    value: any,
    row: InstoreTemplateTableRow
  ) => {
    if (!instoreTemplateRows) return;
    // Create copy of the template before mutating
    const instoreTemplateUpdated = { ...instoreTemplate };
    let rowsToModify = instoreTemplateRows ? [...instoreTemplateRows] : [];

    // Update values in the array from the backend and update state
    if (columnId.startsWith("store_format_")) {
      //  Check if it is a store format amount update
      const [, , storeFormatIdStr, storeFormatName] = columnId.split("_");
      const storeFormatId = Number(storeFormatIdStr);

      const affectedRowDbId =
        row.store_formats_amounts.find(
          (sfa) => sfa.store_format_id === storeFormatId
        )?.db_id ?? null;

      if (!!affectedRowDbId) {
        if (Number(value) === 0) {
          dispatch(removeInstoreTemplateRowLocal(affectedRowDbId));
        } else {
          const rowToModify = rowsToModify.find(
            (rtm) => rtm.id === affectedRowDbId
          );
          dispatch(
            upsertInstoreTemplateRowLocal({
              ...rowToModify,
              amount: Number(value),
            })
          );
        }
      } else {
        if (value !== 0) {
          // Create a placeholder
          dispatch(
            upsertInstoreTemplateRowLocal({
              id: getTempId(),
              template_id: instoreTemplateUpdated.id,
              theme_id: row.theme_id,
              theme: row.theme,
              article_name: row.article_name ?? "",
              store_format_id: storeFormatId,
              store_format: storeFormatName,
              amount: value,
              index: 9999,
            })
          );
        }
      }
      return;
    } else if (columnId === "article_name") {
      // Check if it is a article name update
      if (!value) return;

      const affectedRows = rowsToModify
        .filter(
          (rowToModify) =>
            rowToModify.theme_id === row.theme_id &&
            rowToModify.article_name === row.article_name
        )
        .map((row) => ({ ...row, article_name: value }));

      dispatch(upsertManyInstoreTemplateRowLocal(affectedRows));
      return;
    } else {
      // Nothing found default message
      toast.warning("No updatable fields has been found");
      return;
    }
  };

  const removeItemsLocalStateChangesHandler = (
    row: InstoreTemplateTableRow
  ) => {
    // Update values in the array from the backend and update state
    function collectDbIds(row: InstoreTemplateTableRow): number[] {
      const ids = new Set<number>();
      function walk(node: InstoreTemplateTableRow) {
        node.store_formats_amounts.forEach((sfa) => ids.add(sfa.db_id));
        node.children?.forEach((child) => walk(child));
      }
      walk(row);
      return [...ids]; // return as array
    }

    if (collectDbIds(row).length > 0) {
      dispatch(removeManyInstoreTemplateRowLocal(collectDbIds(row)));
    } else {
      // Nothing found default message
      toast.warning("No items found to delete");
      return;
    }
  };
  //#endregion

  //#region SAVE CHANGES
  const saveChangesHandler = async () => {
    if (!instoreTemplate) return;

    const request: InstoreTemplateRequest = {
      custom_template_id: instoreTemplate.custom_template_id,
      instore_type_id: instoreTemplate.instore_type_id,
      order_type: instoreTemplate.order_type,
      active: instoreTemplate.active,
      from: toBackendDate(instoreTemplate.from),
      until: toBackendDate(instoreTemplate.until),
      template_rows: (instoreTemplateRows ?? []).map(
        (row: InstoreTemplateRow) => ({
          id: row.id > 0 ? row.id : undefined,
          theme_id: row.theme_id,
          store_format_id: row.store_format_id,
          article_name: row.article_name,
          amount: row.amount,
          index: row.index,
        })
      ),
    };

    await dispatch(
      updateInstoreTemplateAsync({
        id: instoreTemplate.id,
        values: request,
      })
    );
  };

  //#endregion

  //#region UPDATE SPECS
  const updateSpecsModalHandler = () => {
    dispatch(openModal(UPDATE_TEMPLATE_SPECS_MODAL));
  };
  //#endregion

  //#region CLEANUP UI
  const cancelCRUDTemplate = () => {
    dispatch(openModal(false));
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal
        type={CREATE_TEMPLATE_ROW_MODAL}
        closeHandler={cancelCRUDTemplate}
      >
        <InstoreTemplateRowForm
          addThemeRowHandler={addThemeRowHandler}
          usedThemes={Array.from(
            new Set(
              (instoreTemplateRows ?? [])
                .map((r: any) => (r.theme ?? "").toLowerCase())
                .filter(Boolean)
            )
          )}
          cancel={cancelCRUDTemplate}
        />
      </MainModal>

      <MainModal
        type={UPDATE_TEMPLATE_SPECS_MODAL}
        closeHandler={cancelCRUDTemplate}
      >
        <InstoreTemplateForm
          instoreTemplateId={instoreTemplate?.id}
          cancelCUD={cancelCRUDTemplate}
          disabled={
            loading &&
            (status.includes("PendingCreateInstoreTemplate") ||
              status.includes("PendingUpdateInstoreTemplate"))
          }
        />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader
          extraHQButtons={
            <button
              disabled={loadingThemes || loading}
              className={classNames(
                "bg-amber-100 hover:bg-amber-300 flex justify-center items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold leading-6 text-amber-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 shadow-sm",
                loadingThemes || loading
                  ? "cursor-not-allowed opacity-50"
                  : "opacity-100"
              )}
              onClick={updateSpecsModalHandler}
              type="button"
            >
              <PencilSquareIcon
                className="-ml-0.5 h-5 w-5"
                aria-hidden="true"
              />
              <p>EDIT SPECS</p>
            </button>
          }
          disabled={loadingThemes || loading}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          title={
            create
              ? "Create Instore Template"
              : copy && instoreTemplate
              ? `Copy ${instoreTemplate.name}`
              : instoreTemplate
              ? `Edit ${instoreTemplate.name}`
              : "Template detail"
          }
        >
          <GoBackButton />
        </PageHeader>

        {tableData && tableData.length > 0 ? (
          <InstoreTemplateDetailTable
            saveServerStateChanges={saveChangesHandler}
            saveLocalStateChanges={(
              columnId: string,
              value: any,
              row: InstoreTemplateTableRow
            ) => saveLocalStateChangesHandler(columnId, value, row)}
            addParentRowHandler={addThemeRowModalClickHandler}
            addChildRowHandler={addArticleRowHandler}
            deleteRowHandler={(row: InstoreTemplateTableRow) =>
              removeItemsLocalStateChangesHandler(row)
            }
            availableFormats={distinctStoreFormats}
            instoreTemplate={instoreTemplate}
            disabled={loadingThemes || loading}
            data={tableData}
          />
        ) : null}
      </MainContainer>
    </Fragment>
  );
  //#endregion
}
